import { System, SystemImpl } from 'minecs';
import type { GameModel } from 'yage/game/GameModel';
import { Transform } from 'yage/schemas/entity/Transform';
import { AirportState, AirportAgent, AirportZone, AirportPath } from '../components/index.js';
import { CONFIG, onPad } from '../domain/config.js';
import { debit, unlockCost, upgradeCost } from '../domain/economy.js';
import { FLOOR_UPGRADE_DWELL, FLOOR_UPGRADE_COOLDOWN, disclosePad } from '../domain/progression.js';
import { state, career, ids, stations, distance, zoneAvailable, feedback, notice } from '../services/world.js';
@System(AirportState)
export class AirportInteractions extends SystemImpl<GameModel> {
    static depth = 4500;
    run = (g: GameModel) => {
        const e = state(g);
        if (!e.loaded) return;
        const ss = stations(g);
        for (const id of ids(g, AirportAgent)) {
            const a = g.getTypedUnsafe(AirportAgent, id), pos = g.getTypedUnsafe(Transform, id), path = g.getTypedUnsafe(AirportPath, id);
            const near = ids(g, AirportZone).filter(z => zoneAvailable(g, z) && onPad(g.getTypedUnsafe(AirportZone, z).kind, pos, g.getTypedUnsafe(Transform, z))).sort((x, y) => distance(pos, g.getTypedUnsafe(Transform, x)) - distance(pos, g.getTypedUnsafe(Transform, y)) || x - y);
            const zone = near[0] ?? -1;
            if (zone !== a.pad) { a.pad = zone; a.dwell = 0; a.completedPad = -1; }
            if (zone < 0 || path.moving) { a.dwell = 0; a.building = -1; continue; }
            if (a.inputLocked) { a.dwell = 0; continue; }
            const z = g.getTypedUnsafe(AirportZone, zone);
            a.dwell++;
            if (z.kind === 'build') {
                const s = ss[z.stage];
                if (a.intent !== zone || a.building !== z.stage || s.unlocked || a.dwell < 30) continue;
                const remaining = unlockCost(z.stage, e) - s.invested;
                const amount = Math.min(career(g).money, remaining, Math.max(1, Math.ceil(unlockCost(z.stage, e) / 35)));
                if (amount > 0) { debit(career(g), amount); s.invested += amount; }
                if (s.invested >= unlockCost(z.stage, e)) {
                    // Two calculations at the build pad open it one speed level up.
                    const boost = a.ticketChoice === 'build-boost' && a.ticketStage === z.stage ? 1 : 0;
                    if (boost) a.ticketChoice = '';
                    Object.assign(s, { level: 1 + boost, unlocked: true, invested: 0, lanes: 1, speedLevel: boost, incomeLevel: 0 });
                    a.building = -1; a.dwell = 0; a.lastPurchaseFrame = g.frame;
                    notice(g, `${CONFIG.stages[z.stage].name} is open. Work here or hire its crew.`, 2);
                } else if (!amount) feedback(g, id, 'Collect more cash to continue construction.');
                continue;
            }
            // Floor speed pads: stand still for a moment and the desk beside it gets one level faster.
            if (z.kind === 'upgrade' && disclosePad(e, ss, 'upgrade', z.stage)) {
                const s = ss[z.stage];
                if (a.dwell < FLOOR_UPGRADE_DWELL || s.upgradeUntil > g.frame) continue;
                const cost = upgradeCost(s, 'speed', e);
                if (!Number.isFinite(cost)) continue;
                if (!debit(career(g), cost)) {
                    if (a.dwell === FLOOR_UPGRADE_DWELL) feedback(g, id, `Need ${cost - career(g).money} more bills for a faster ${CONFIG.stages[z.stage].name}.`);
                    continue;
                }
                s.speedLevel = Math.min(20, s.speedLevel + 1); s.level++;
                s.upgradeUntil = g.frame + FLOOR_UPGRADE_COOLDOWN;
                a.dwell = 0; a.lastPurchaseFrame = g.frame; e.upgrades++; e.revision++;
                feedback(g, id, `Improved. ${CONFIG.stages[z.stage].name} speed level ${s.speedLevel}.`);
                continue;
            }
        }
    };
}
